import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Default (English) messages of the admin-only « Kubernetes » tab of an Odoo
 * project page (`ProjectKubernetesTab.tsx`). Same arrangement as
 * `components/projectEnvironments/translation.ts`: the French resource below
 * overrides the default language, with `full: true`, and is registered by a
 * module on the `app` plugin (`plugin.tsx`). Kubernetes' own words (a phase,
 * `Synced`, `Ready`, a kind) are shown as Kubernetes says them.
 */
export const projectKubernetesTranslationRef = createTranslationRef({
  id: 'waadoo-project-kubernetes',
  messages: {
    // The BCP 47 locale of the figures — see `format.locale` in the
    // environments translation for why it is a message.
    'format.locale': 'en',
    // The small badge after the tab's title in the project header.
    'tab.adminBadge': 'admin',
    'restricted.title': 'Administrators only',
    'restricted.description':
      'This tab shows the Kubernetes objects of the project; it is reserved for the platform administrators.',
    loading: 'Reading the cluster…',
    'error.failed': 'The Kubernetes objects of the project could not be read.',
    refresh: 'Refresh',
    readAt: 'Read {{age}} ago',
    truncated: 'Only the first {{shown}} namespaces are shown.',
    'strip.label': 'Project summary',
    'strip.namespaces': 'Namespaces',
    'strip.sleeping_one': '{{count}} environment asleep',
    'strip.sleeping_other': '{{count}} environments asleep',
    'strip.pods': 'Pods',
    'strip.podsReady': '{{ready}} / {{total}} ready',
    'strip.restarts_one': '{{count}} restart',
    'strip.restarts_other': '{{count}} restarts',
    'strip.applications': 'ArgoCD applications',
    'strip.applicationsValue': '{{ok}} / {{total}} Synced · Healthy',
    'strip.crossplane': 'Crossplane',
    'strip.crossplaneValue': '{{ready}} / {{total}} Ready',
    'strip.quota': 'Reserved / quota',
    'strip.quotaCpu': '{{used}} / {{hard}} CPU',
    'strip.quotaMemory': '{{used}} / {{hard}} GiB',
    'strip.none': 'none',
    'problems.title': 'To look at',
    'problems.empty': 'Nothing to report',
    'problems.listLabel': 'Problems of every environment',
    'problems.common': 'Project (shared)',
    'severity.critical': 'Action needed',
    'severity.warning': 'To watch',
    // One problem in words: `detail` is Kubernetes' own (a reason, a phase…).
    'problem.podNotReady': 'Not ready ({{detail}})',
    'problem.podFailed': 'Failed ({{detail}})',
    'problem.crashLoop_one': 'Restarting in a loop ({{count}} restart)',
    'problem.crashLoop_other': 'Restarting in a loop ({{count}} restarts)',
    'problem.imagePull': 'Image cannot be pulled ({{detail}})',
    'problem.restarts_one': '{{count}} restart within 24 h ({{detail}})',
    'problem.restarts_other': '{{count}} restarts within 24 h ({{detail}})',
    'problem.warningEvent': 'Warning event {{detail}} ×{{value}}',
    'problem.argoSync': 'ArgoCD: {{detail}}',
    'problem.argoHealth': 'ArgoCD: {{detail}}',
    'problem.crossplaneReady': 'Not Ready ({{detail}})',
    'problem.crossplaneSynced': 'Not Synced ({{detail}})',
    'problem.cnpgUnhealthy': 'PostgreSQL: {{detail}}',
    'problem.pvcNotBound': 'Volume not bound ({{detail}})',
    'problem.certNotReady': 'Certificate not ready ({{detail}})',
    'problem.certExpiring_one': 'Certificate expires in {{count}} day',
    'problem.certExpiring_other': 'Certificate expires in {{count}} days',
    'problem.certExpired': 'Certificate expired',
    'problem.quotaHigh': 'Quota {{detail}} at {{value}} %',
    'problem.unreadable': 'Could not be listed: {{detail}}',
    'envs.title': 'Namespaces',
    'envs.listLabel': 'Environments of the project',
    'envs.sleeping': 'asleep',
    'envs.problems_one': '{{count}} problem',
    'envs.problems_other': '{{count}} problems',
    'envs.ok': 'nothing to report',
    'envs.common': 'Project (shared)',
    'envs.commonHint': 'ArgoCD, AppProject',
    'env.quota': 'quota {{cpu}} CPU · {{memory}} GiB · {{pods}} pods',
    'env.usage': 'using {{cpu}} CPU · {{memory}} GiB',
    'env.noArgo': 'No ArgoCD application',
    'env.unreadable': 'Could not be listed: {{kinds}}',
    'env.sleepingTitle': 'This environment is asleep',
    'env.sleepingDescription':
      'It has no namespace: its objects went with its ArgoCD application. Its backup bucket is kept.',
    'env.commonTitle': 'Project (shared)',
    'env.commonDescription':
      'The ArgoCD objects of the project that are not an environment’s own.',
    'section.listLabel': 'Objects of {{env}}',
    'section.workloads': 'Workloads',
    'section.network': 'Network',
    'section.storage': 'Storage',
    'section.configuration': 'Configuration',
    'section.crossplane': 'Crossplane',
    'section.events': 'Events',
    'section.empty': 'No object.',
    'column.type': 'Type',
    'column.name': 'Name',
    'column.state': 'State',
    'column.detail': 'Detail',
    'column.age': 'Age',
    'column.yaml': 'YAML',
    'column.reason': 'Reason',
    'column.object': 'Object',
    'column.message': 'Message',
    'column.lastSeen': 'Last seen',
    'kind.Cluster': 'CNPG cluster',
    'row.restarts_one': '{{count}} restart',
    'row.restarts_other': '{{count}} restarts',
    'row.keys_one': '{{count}} key',
    'row.keys_other': '{{count}} keys',
    'row.expiresIn_one': 'expires in {{count}} day',
    'row.expiresIn_other': 'expires in {{count}} days',
    'row.expired': 'expired',
    'row.lastBackup': 'last backup run {{age}} ago',
    'yaml.show': 'YAML',
    'yaml.label': 'YAML of {{kind}} {{name}}',
    'yaml.loading': 'Loading the YAML…',
    'yaml.failed': 'The YAML could not be read.',
    'yaml.note':
      'Read-only. managedFields removed; values of the ConfigMaps, of the environment variables and of the Helm values hidden; Secrets never shown.',
    'events.empty': 'No Warning event in the last 6 hours.',
  },
});

const resource = createTranslationMessages({
  full: true,
  ref: projectKubernetesTranslationRef,
  messages: {
    'format.locale': 'fr-FR',
    'tab.adminBadge': 'admin',
    'restricted.title': 'Réservé aux administrateurs',
    'restricted.description':
      'Cet onglet présente les objets Kubernetes du projet ; il est réservé aux administrateurs de la plateforme.',
    loading: 'Lecture du cluster…',
    'error.failed': 'Les objets Kubernetes du projet n’ont pas pu être lus.',
    refresh: 'Actualiser',
    readAt: 'Lu il y a {{age}}',
    truncated: 'Seuls les {{shown}} premiers namespaces sont affichés.',
    'strip.label': 'Résumé du projet',
    'strip.namespaces': 'Namespaces',
    'strip.sleeping_one': '{{count}} environnement en sommeil',
    'strip.sleeping_other': '{{count}} environnements en sommeil',
    'strip.pods': 'Pods',
    'strip.podsReady': '{{ready}} / {{total}} prêts',
    'strip.restarts_one': '{{count}} redémarrage',
    'strip.restarts_other': '{{count}} redémarrages',
    'strip.applications': 'Applications ArgoCD',
    'strip.applicationsValue': '{{ok}} / {{total}} Synced · Healthy',
    'strip.crossplane': 'Crossplane',
    'strip.crossplaneValue': '{{ready}} / {{total}} Ready',
    'strip.quota': 'Réservé / quota',
    'strip.quotaCpu': '{{used}} / {{hard}} CPU',
    'strip.quotaMemory': '{{used}} / {{hard}} Gio',
    'strip.none': 'aucun',
    'problems.title': 'À regarder',
    'problems.empty': 'Rien à signaler',
    'problems.listLabel': 'Problèmes de tous les environnements',
    'problems.common': 'Projet (commun)',
    'severity.critical': 'À traiter',
    'severity.warning': 'À surveiller',
    'problem.podNotReady': 'Pas prêt ({{detail}})',
    'problem.podFailed': 'En échec ({{detail}})',
    'problem.crashLoop_one': 'Redémarre en boucle ({{count}} redémarrage)',
    'problem.crashLoop_other': 'Redémarre en boucle ({{count}} redémarrages)',
    'problem.imagePull': 'Image impossible à récupérer ({{detail}})',
    'problem.restarts_one': '{{count}} redémarrage en 24 h ({{detail}})',
    'problem.restarts_other': '{{count}} redémarrages en 24 h ({{detail}})',
    'problem.warningEvent': 'Événement Warning {{detail}} ×{{value}}',
    'problem.argoSync': 'ArgoCD : {{detail}}',
    'problem.argoHealth': 'ArgoCD : {{detail}}',
    'problem.crossplaneReady': 'Pas Ready ({{detail}})',
    'problem.crossplaneSynced': 'Pas Synced ({{detail}})',
    'problem.cnpgUnhealthy': 'PostgreSQL : {{detail}}',
    'problem.pvcNotBound': 'Volume non attaché ({{detail}})',
    'problem.certNotReady': 'Certificat pas prêt ({{detail}})',
    'problem.certExpiring_one': 'Certificat expirant dans {{count}} jour',
    'problem.certExpiring_other': 'Certificat expirant dans {{count}} jours',
    'problem.certExpired': 'Certificat expiré',
    'problem.quotaHigh': 'Quota {{detail}} à {{value}} %',
    'problem.unreadable': 'Lecture impossible : {{detail}}',
    'envs.title': 'Namespaces',
    'envs.listLabel': 'Environnements du projet',
    'envs.sleeping': 'sommeil',
    'envs.problems_one': '{{count}} problème',
    'envs.problems_other': '{{count}} problèmes',
    'envs.ok': 'rien à signaler',
    'envs.common': 'Projet (commun)',
    'envs.commonHint': 'ArgoCD, AppProject',
    'env.quota': 'quota {{cpu}} CPU · {{memory}} Gio · {{pods}} pods',
    'env.usage': 'consommé {{cpu}} CPU · {{memory}} Gio',
    'env.noArgo': 'Aucune application ArgoCD',
    'env.unreadable': 'Lecture impossible : {{kinds}}',
    'env.sleepingTitle': 'Cet environnement est en sommeil',
    'env.sleepingDescription':
      'Il n’a pas de namespace : ses objets sont partis avec son application ArgoCD. Son bucket de sauvegarde est conservé.',
    'env.commonTitle': 'Projet (commun)',
    'env.commonDescription':
      'Les objets ArgoCD du projet qui n’appartiennent pas à un seul environnement.',
    'section.listLabel': 'Objets de {{env}}',
    'section.workloads': 'Charges de travail',
    'section.network': 'Réseau',
    'section.storage': 'Stockage',
    'section.configuration': 'Configuration',
    'section.crossplane': 'Crossplane',
    'section.events': 'Événements',
    'section.empty': 'Aucun objet.',
    'column.type': 'Type',
    'column.name': 'Nom',
    'column.state': 'État',
    'column.detail': 'Détail',
    'column.age': 'Âge',
    'column.yaml': 'YAML',
    'column.reason': 'Raison',
    'column.object': 'Objet',
    'column.message': 'Message',
    'column.lastSeen': 'Vu',
    'kind.Cluster': 'Cluster CNPG',
    'row.restarts_one': '{{count}} redémarrage',
    'row.restarts_other': '{{count}} redémarrages',
    'row.keys_one': '{{count}} clé',
    'row.keys_other': '{{count}} clés',
    'row.expiresIn_one': 'expire dans {{count}} jour',
    'row.expiresIn_other': 'expire dans {{count}} jours',
    'row.expired': 'expiré',
    'row.lastBackup': 'dernière sauvegarde lancée il y a {{age}}',
    'yaml.show': 'YAML',
    'yaml.label': 'YAML de {{kind}} {{name}}',
    'yaml.loading': 'Chargement du YAML…',
    'yaml.failed': 'Le YAML n’a pas pu être lu.',
    'yaml.note':
      'Lecture seule. managedFields retirés ; valeurs des ConfigMap, des variables d’environnement et des values Helm masquées ; Secrets jamais affichés.',
    'events.empty': 'Aucun événement Warning depuis 6 heures.',
  },
});

export const projectKubernetesFrTranslation = TranslationBlueprint.make({
  name: 'project-kubernetes-fr',
  params: { resource },
});
